import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Me, Permission } from '#shared';
import { useQueryClient } from '@tanstack/react-query';
import { acceptSession, api, clearSession, refreshSession, setSessionListener, setUnavailableListener } from './api';

/** 'unavailable': the server couldn't be reached while opening the app (we keep retrying). */
type Status = 'loading' | 'unavailable' | 'signedOut' | 'signedIn';

interface AuthValue {
  status: Status;
  me: Me | null;
  /** Returns a challenge when the account uses two-step sign-in; finish with loginWithCode. */
  login: (email: string, password: string, remember: boolean) => Promise<{ challenge?: string }>;
  loginWithCode: (challenge: string, code: string, trustDevice: boolean) => Promise<void>;
  logout: () => Promise<void>;
  reloadMe: () => Promise<void>;
  retry: () => void;
  can: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);
// Signing out in one tab signs out every tab.
const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('aicrm-auth') : null;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [me, setMe] = useState<Me | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    setSessionListener((next) => {
      setMe(next);
      setStatus(next ? 'signedIn' : 'signedOut');
      if (!next) queryClient.clear();
    });
    // Opening the app with no answer from the server: say so (instead of "Loading…" forever) and keep trying.
    setUnavailableListener(() => setStatus((s) => (s === 'loading' || s === 'unavailable' ? 'unavailable' : s)));
    void refreshSession();
    const onMessage = (e: MessageEvent) => {
      if (e.data === 'logout') clearSession();
      if (e.data === 'login') void refreshSession();
    };
    channel?.addEventListener('message', onMessage);
    return () => channel?.removeEventListener('message', onMessage);
  }, [queryClient]);

  const login = useCallback(async (email: string, password: string, remember: boolean) => {
    const res = await api.post<{ accessToken: string; me: Me } | { twoFactorRequired: true; challenge: string }>('/auth/login', { email, password, remember });
    if ('challenge' in res) return { challenge: res.challenge };
    acceptSession(res.accessToken, res.me);
    channel?.postMessage('login');
    return {};
  }, []);

  const loginWithCode = useCallback(async (challenge: string, code: string, trustDevice: boolean) => {
    const res = await api.post<{ accessToken: string; me: Me }>('/auth/login/2fa', { challenge, code, trustDevice });
    acceptSession(res.accessToken, res.me);
    channel?.postMessage('login');
  }, []);

  const logout = useCallback(async () => {
    await api.post('/auth/logout').catch(() => {});
    clearSession();
    channel?.postMessage('logout');
  }, []);

  const reloadMe = useCallback(async () => {
    setMe(await api.get<Me>('/auth/me'));
  }, []);

  const retry = useCallback(() => void refreshSession(), []);
  useEffect(() => {
    if (status !== 'unavailable') return;
    const t = setInterval(retry, 5000);
    return () => clearInterval(t);
  }, [status, retry]);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      me,
      login,
      loginWithCode,
      logout,
      reloadMe,
      retry,
      can: (p) => !!me?.permissions.includes(p),
    }),
    [status, me, login, loginWithCode, logout, reloadMe, retry],
  );

  // Company colour: lets each customer see their own brand colour across the app.
  useEffect(() => {
    document.documentElement.style.setProperty('--brand', me?.tenant?.primaryColor || '#4f46e5');
  }, [me?.tenant?.primaryColor]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
